import {
  Box,
  CircularProgress,
  Divider,
  Grid,
  Skeleton,
  Typography,
} from "@mui/material";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useEffect, useState } from "react";
import { useLocation } from "react-router-dom";
import useAPI from "../../../../../api/hooks/useAPI";
import useColors from "../../../../../api/hooks/useColors";
import useMSSP from "../../../../../api/hooks/useMSSP";
import {
  controlKeys,
  entityKeys,
  vulnerabilityKeys,
} from "../../../../../api/queryKeys";
import CustomToolTip from "../../../../../components/CustomTooltip";
import ConfirmationDialog from "../../../../../components/dialogs/ConfirmationDialog";
import ResizableDrawer from "../../../../../components/ResizableDrawer";
import { Toast } from "../../../../../components/Toast";
import SvgToIcon from "../../../../../icons/SvgToIcon";
import { entityTypes } from "../../../../../utils/constants";
import { daysPastSince } from "../../../../../utils/timestamp";
import RecentlyFoundLabel, {
  isRecentlyFound,
} from "../../../../posture/controls/components/RecentlyFoundLabel";
import { EntityTypeInfo } from "../constants";
import ControlsDrawer from "../ControlsDrawer";
import CriticalAssetsListSection from "../EntityDrawer/CriticalAssetsListSection";
import SourcesListSection from "../EntityDrawer/SourcesListSection";
import TabsView from "../EntityDrawer/Tabsview";
import VulnerabilitiesDrawer from "../VulnerabilitiesDrawer";
import EntityFavoritesButton from "./EntityFavoritesButton";
import EntityRiskBreakdown from "./EntityRiskBreakdown";
import LabelsListSection from "./LabelsListSection";
import RiskSummarySection from "./RiskSummarySection";
import TagsListSection from "./TagsListSection";

const EntityTabsSkeleton = () => {
  return (
    <Box sx={{ display: "flex", height: "100%", marginBottom: "80px" }}>
      <Box sx={{ borderRight: 1, borderColor: "divider", pr: 2 }}>
        {Array.from({ length: 8 }).map((_, index) => (
          <Skeleton
            key={index}
            variant="rounded"
            width={180}
            height={28}
            sx={{ mb: 1 }}
          />
        ))}
      </Box>
      <Box sx={{ flexGrow: 1, pl: 2 }}>
        <Skeleton variant="text" width="55%" height={32} />
        <Skeleton variant="rounded" width="100%" height={320} sx={{ mt: 1 }} />
      </Box>
    </Box>
  );
};

const EntityDrawer = ({
  openDrawer,
  selectedEntity,
  onClose,
  entityDetailsIsLoading = false,
}) => {
  const colors = useColors();
  const {
    trackEvent,
    updateEntityExclusion,
    refreshEntitiesOverview,
    fetchControlsBatch,
    fetchVulnerabilitiesByEntityId,
  } = useAPI();
  const [riskAnchorEl, setRiskAnchorEl] = useState(null);
  const [openControlDrawer, setOpenControlDrawer] = useState(false);
  const [openVulnerabilitiesDrawer, setOpenVulnerabilitiesDrawer] =
    useState(false);
  const [openDialog, setOpenDialog] = useState(false);
  const location = useLocation();
  const queryClient = useQueryClient();
  const { hasMultipleSelectedTenants } = useMSSP();
  const showEntityDetailsLoading =
    Boolean(openDrawer) && Boolean(entityDetailsIsLoading);

  const EntityIcon = selectedEntity?.entity_type
    ? EntityTypeInfo[selectedEntity?.entity_type].icon
    : null;

  const controlsQuery = useQuery({
    queryKey: controlKeys.batchControls(
      selectedEntity?.related_control_ids || []
    ),
    queryFn: () =>
      fetchControlsBatch(selectedEntity?.related_control_ids || []),
    select: (response) => response.data,
  });

  const {
    data: entityVulnerabilities,
    isLoading: entityVulnerabilitiesIsLoading,
  } = useQuery({
    queryKey: vulnerabilityKeys.vulnerabilitiesByEntityId(
      selectedEntity?.entity_id
    ),
    queryFn: () => {
      return fetchVulnerabilitiesByEntityId(selectedEntity?.entity_id);
    },
    select: (response) =>
      response.data.map((vuln) => ({
        ...vuln,
        id: vuln.vulnerability_id,
        days_found: daysPastSince(
          vuln.published_date?.timestamp_utc_human_readable
        ),
      })),
    enabled:
      selectedEntity !== null &&
      selectedEntity?.entity_id !== undefined &&
      !hasMultipleSelectedTenants,
  });

  const updateExclude = async ({ entityId, exclude }) => {
    try {
      const response = await updateEntityExclusion(entityId, exclude);
      const newStateDescription = exclude ? "excluded" : "included";
      Toast.success(`The entity was successfully ${newStateDescription}.`);
      refreshEntitiesOverview().then(() => {
        queryClient.invalidateQueries({
          queryKey: entityKeys.entitiesOverview(),
        });
      });
      return response.data;
    } catch (err) {
      Toast.error("Error updating entity. Please try again.");
      return [];
    }
  };

  const { mutate: mutateExcluded } = useMutation({
    mutationFn: updateExclude,
    onSuccess: () => {
      // Invalidate the entity and all controls to update the UI from environment and control
      queryClient.invalidateQueries({
        queryKey: entityKeys.all,
      });
      queryClient.invalidateQueries({
        queryKey: controlKeys.all,
      });
    },
  });

  const handleExcludeFromAllClick = async () => {
    const userState = selectedEntity?.is_excluded_from_all
      ? "included"
      : "excluded";
    trackEvent(selectedEntity?.entity_id + `User ${userState} from all gaps`);
    let toggleExcluded = !selectedEntity.is_excluded_from_all;
    await mutateExcluded({
      entityId: selectedEntity?.entity_id,
      exclude: toggleExcluded,
    });
    handleDialogClose();
    refreshEntitiesOverview().then(() => {
      queryClient.invalidateQueries({
        queryKey: entityKeys.entitiesOverview(),
      });
    });
  };

  const handleDialogOpen = () => {
    selectedEntity?.is_excluded_from_all
      ? handleExcludeFromAllClick()
      : setOpenDialog(true);
  };

  const handleDialogClose = () => {
    setOpenDialog(false);
  };

  useEffect(() => {
    if (openDrawer) {
      if (selectedEntity) {
        // NOTE: this track message is monitored by Mixpanel to collect data on user engagement - DO NOT CHANGE IT
        trackEvent(
          `User opened drawer for entity id ${selectedEntity.entity_id}`
        );
      }
    }
  }, [openDrawer, selectedEntity, trackEvent]);

  useEffect(() => {
    const localSearchParams = new URLSearchParams(location.search);
    const show_gaps =
      localSearchParams.get("gaps") &&
      decodeURIComponent(localSearchParams.get("gaps")) ===
        selectedEntity?.entity_id;

    const show_vulnerabilities =
      localSearchParams.get("vulnerabilities") &&
      decodeURIComponent(localSearchParams.get("vulnerabilities")) ===
        selectedEntity?.entity_id;
    if (show_vulnerabilities) {
      setOpenVulnerabilitiesDrawer(true);
    } else {
      setOpenVulnerabilitiesDrawer(false);
    }
    if (show_gaps) {
      setOpenControlDrawer(true);
    } else {
      setOpenControlDrawer(false);
    }
  }, [location, selectedEntity]);

  const getEntityTypeForDisplay = () => {
    return entityTypes().find(
      (entity) => entity.type.toString() === selectedEntity?.entity_type
    )?.name;
  };

  return (
    <ResizableDrawer
      initialWidth={850}
      minWidth={850}
      PaperProps={{
        style: {
          borderRadius: "8px 0 0 0",
        },
      }}
      anchor="right"
      open={openDrawer}
      onClose={onClose}
    >
      <Box
        sx={{
          width: "100%",
          backgroundColor: colors.grey_50,
          height: "100%",
          padding: "24px",
          display: "flex",
          flexDirection: "column",
          gap: 2,
        }}
      >
        <Grid container gap={2}>
          <Grid container display="flex" alignItems="center" gap={2}>
            <Grid item display="flex" gap={2} alignItems="center" height="36px">
              <CustomToolTip title={getEntityTypeForDisplay()}>
                <Box height="36px">
                  {EntityIcon && (
                    <SvgToIcon
                      IconLogo={EntityIcon}
                      style={{ width: "36px", height: "36px" }}
                    />
                  )}
                </Box>
              </CustomToolTip>
              <Typography variant="h5" sx={{ lineHeight: "30px" }}>
                {selectedEntity?.display_name}
              </Typography>
              {showEntityDetailsLoading && (
                <CircularProgress size={16} sx={{ ml: 1 }} />
              )}
              <EntityFavoritesButton entityId={selectedEntity?.entity_id} />
            </Grid>
            <Grid item xs={1}>
              {!showEntityDetailsLoading &&
                isRecentlyFound(
                  selectedEntity?.first_collected_timestamp?.timestamp_utc
                ) && (
                  <RecentlyFoundLabel
                    foundTimestamp={
                      selectedEntity?.first_collected_timestamp
                        ?.timestamp_utc || ""
                    }
                  />
                )}
            </Grid>
            <Grid item ml="auto">
              {showEntityDetailsLoading ? (
                <Skeleton variant="rounded" width={180} height={32} />
              ) : (
                <SourcesListSection selectedEntity={selectedEntity} />
              )}
            </Grid>
          </Grid>
          {!hasMultipleSelectedTenants && (
            <Grid item xs={12}>
              <Divider orientation="horizontal" flexItem />
            </Grid>
          )}

          {!hasMultipleSelectedTenants &&
            (showEntityDetailsLoading ? (
              <>
                <Grid item xs={12}>
                  <Skeleton variant="rounded" height={90} />
                </Grid>
                <Grid item xs={12}>
                  <Skeleton variant="rounded" height={72} />
                </Grid>
                <Grid item xs={12}>
                  <Skeleton variant="rounded" height={72} />
                </Grid>
              </>
            ) : (
              <>
                <RiskSummarySection
                  entityData={selectedEntity}
                  onExcludeClick={handleDialogOpen}
                  setRiskAnchorEl={setRiskAnchorEl}
                  riskAnchorEl={riskAnchorEl}
                />
                <CriticalAssetsListSection selectedEntity={selectedEntity} />
                <LabelsListSection selectedEntity={selectedEntity} />
                <TagsListSection selectedEntity={selectedEntity} />
              </>
            ))}
          <Grid item xs={12}>
            <Divider orientation="horizontal" flexItem />
          </Grid>
          <Grid item xs={12}>
            <Typography variant="body1" fontWeight="400"></Typography>
            {selectedEntity &&
              (showEntityDetailsLoading ? (
                <EntityTabsSkeleton />
              ) : (
                <TabsView selectedEntity={selectedEntity} />
              ))}
          </Grid>
        </Grid>
      </Box>
      <EntityRiskBreakdown
        entity={selectedEntity}
        anchorEl={riskAnchorEl}
        setAnchorEl={setRiskAnchorEl}
      />
      <ControlsDrawer
        entity={selectedEntity}
        controlsQuery={controlsQuery}
        openDrawer={openControlDrawer}
        setOpenDrawer={setOpenControlDrawer}
        relatedControlIds={selectedEntity?.related_control_ids || []}
      />
      <VulnerabilitiesDrawer
        open={openVulnerabilitiesDrawer}
        setOpen={setOpenVulnerabilitiesDrawer}
        entityVulnerabilities={entityVulnerabilities}
        entityVulnerabilitiesIsLoading={entityVulnerabilitiesIsLoading}
      />
      <ConfirmationDialog
        openDialog={openDialog}
        handleDialogClose={handleDialogClose}
        handleConfirm={handleExcludeFromAllClick}
        dialogContentFirstLine={`You are about to exclude ${selectedEntity?.display_name} from all risk evaluations.
        This will remove all existing misconfiguration and risk assessments and exclude the entity from future evaluations.
        You can undo this action anytime by selecting 'Include in Risk'.`}
        dialogContentSecondLine=""
        confirmationButtonText="Exclude from Risk"
        destructive={true}
      />
    </ResizableDrawer>
  );
};

export default EntityDrawer;
